import React from 'react';
import styled, { keyframes } from 'styled-components';

import { HybridSlider } from 'src/components/hybrid-carousel/hybrid-carousel-slider';
import useUI from 'hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { Typography } from 'src/typography';
import { CategoriesTypeOption, RadiusOption } from 'src/gql/graphql';
import type { DispensaryMenuCategory } from 'src/dispensary/hooks/use-dispensary-categories';

import { getSpecialsCategoryHref } from 'src/dispensary/specials/specials-category-tiles-utils';
import { SpecialsCategoryCard } from './specials-category-card';

const SKELETON_CARD_COUNT = 6;

type SpecialsCategoryGridProps = {
  categories: DispensaryMenuCategory[];
  dispensaryCName: string;
  heading: string;
  loading?: boolean;
};

export function SpecialsCategoryGrid({
  categories,
  dispensaryCName,
  heading,
  loading = false,
}: SpecialsCategoryGridProps): JSX.Element | null {
  const { dispensaryRootUrl } = useUI();
  const { dispensary } = useDispensary();
  const { type: categoryType, style: categoryStyle } = dispensary?.webCustomizationSettingsV2?.categories ?? {
    type: CategoriesTypeOption.large,
    style: RadiusOption.roundedRectangle,
  };

  if (loading) {
    return (
      <Container data-testid='specials-category-grid-loading'>
        <SectionHeading size='medium' tag='h1'>
          {heading}
        </SectionHeading>
        <SkeletonRow role='status' aria-live='polite' aria-busy='true' aria-label='Loading categories'>
          {Array.from({ length: SKELETON_CARD_COUNT }).map((_val, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <SkeletonCard key={index} aria-hidden='true'>
              <SkeletonLabel />
              <SkeletonImage />
            </SkeletonCard>
          ))}
        </SkeletonRow>
      </Container>
    );
  }

  if (categories.length === 0) {
    return null;
  }

  return (
    <Container data-testid='specials-category-grid'>
      <SectionHeading size='medium' tag='h2' id='specials-category-grid-heading'>
        {heading}
      </SectionHeading>
      <HybridSlider carouselPadding={0}>
        {categories.map((category) => {
          const href = getSpecialsCategoryHref(dispensaryRootUrl, dispensaryCName, category.key);
          return (
            <TileWrapper key={category.key} role='group' aria-roledescription='slide' aria-label={category.label}>
              <SpecialsCategoryCard category={category} href={href} type={categoryType} style={categoryStyle} />
            </TileWrapper>
          );
        })}
      </HybridSlider>
    </Container>
  );
}

const SectionHeading = styled(Typography.Heading)`
  margin: 0;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 16px;
`;

// Each SpecialsCategoryCard variant (large/compact/stacked/text) sets its own explicit width,
// so the wrapper just hugs its content rather than forcing a fixed slide width.
const TileWrapper = styled.div`
  flex-shrink: 0;
  width: auto;
  margin-right: 12px;
`;

const skeletonPulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`;

const SkeletonRow = styled.div`
  display: flex;
  gap: 12px;
  overflow: hidden;
  margin-top: 20px;
`;

const SkeletonCard = styled.div`
  flex-shrink: 0;
  width: 184px;
  border: 1px solid ${({ theme }) => theme.colors?.greyBorder ?? '#e3e7e9'};
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SkeletonLabel = styled.div`
  animation: ${skeletonPulse} 1s linear infinite;
  background-color: ${({ theme }) => theme.colors?.grey?.[95] ?? '#e3e7e9'};
  border-radius: 4px;
  height: 24px;
  width: 70%;
`;

const SkeletonImage = styled.div`
  animation: ${skeletonPulse} 1s linear infinite;
  background-color: ${({ theme }) => theme.colors?.grey?.[95] ?? '#e3e7e9'};
  border-radius: 8px;
  height: 77px;
  width: 100%;
`;
